/**
 * Code Refactor Executor
 *
 * Handles the 'code-refactor' and 'pipeline-refactor' node execution.
 * Calls the ModernizeIT API Layer /coderefactor endpoint.
 *
 * API Flow:
 * 1. POST /coderefactor → runs analysis, returns job_id and results
 * 2. If mode is 'transform' or 'full', POST /coderefactor/transform
 * 3. Poll GET /coderefactor/{job_id}/status if needed
 * 4. GET /coderefactor/{job_id}/results → retrieve full results
 *
 * This node analyzes generated Java code from Code Analysis and:
 * - Detects refactoring patterns (field groupings, method groupings, etc.)
 * - Uses AI (Claude) to interpret patterns semantically
 * - Generates recipes for transformations
 * - Can split one Java file into multiple classes
 */

import { pollJobUntilComplete } from '../../utils/jobPoller.js'
import { getAuthHeaders, handleApiError } from './authHelper.js'

/**
 * Execute the code refactor node
 *
 * @param {Object} config - Node configuration
 * @param {Object} context - Workflow execution context
 * @param {Function} onProgress - Progress callback (progress: 0-100, message: string)
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @returns {Promise<Object>} - Execution result with job info and artifacts
 */
export async function execute(config, context, onProgress = () => {}, signal = null) {
  const apiBaseUrl = context.api_base_url

  onProgress(0, 'Preparing code refactor analysis...')

  // Check for abort before starting
  if (signal?.aborted) {
    throw new Error('Execution cancelled')
  }

  // Determine mode from checkbox
  // apply_transformations: true (default) = full mode (analyze + transform)
  // apply_transformations: false = analyze only
  const applyTransformations = config.apply_transformations !== false // Default true
  const mode = applyTransformations ? 'full' : 'analyze'

  // 1. Build the request body
  // Worker-mode: Single call with auto_transform handles both analyze + transform

  // Convert recipes_to_apply from UI format to API format
  // API expects: Optional[List[str]] (list of recipe names, or null for all)
  // UI sends: "all" | "high_confidence" | "extract_class" | null
  let recipesToApply = null
  if (applyTransformations && config.recipes_to_apply) {
    if (config.recipes_to_apply === 'all') {
      // "all" means null (API applies all recipes)
      recipesToApply = null
    } else {
      // Convert other UI values to list format
      // For now, treat filter keywords as null (apply all matching that type)
      // Future: API could support filter keywords or we enumerate specific recipe IDs
      recipesToApply = null
    }
  }

  const requestBody = {
    scout_account_id: context.scout_account_id,
    application_name: context.application_name,
    user_id: context.user_id, // REQUIRED 2026-01-14: For AWS credentials lookup
    // Optional: specific class or all classes
    class_name: config.class_name || null,
    // Mode: analyze (worker handles transform via auto_transform)
    mode: 'analyze',
    // AI analysis toggle
    use_ai: config.use_ai !== false, // Default true
    // Auto-transform: Worker will apply transformations automatically if enabled
    auto_transform: applyTransformations,
    // Recipes for transform phase (must be list or null, never string)
    recipes_to_apply: recipesToApply,
  }

  let jobResponse
  let jobId

  // Phase 1: Analyze (always run first)
  onProgress(5, 'Submitting refactor analysis job...')

  try {
    const response = await fetch(`${apiBaseUrl}/coderefactor`, {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(requestBody),
      signal,
    })

    if (!response.ok) {
      await handleApiError(response)
    }

    jobResponse = await response.json()

    if (jobResponse.success === false) {
      throw new Error(jobResponse.error || 'Code refactor analysis failed')
    }

  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Execution cancelled')
    }
    if (error.message.includes('Failed to fetch')) {
      throw new Error(`Cannot connect to API at ${apiBaseUrl}. Is the server running?`)
    }
    throw error
  }

  jobId = jobResponse.job_id
  if (!jobId) {
    throw new Error('API did not return a job_id')
  }

  onProgress(10, `Analysis job started: ${jobId}`)

  // Check if job completed immediately (synchronous execution)
  if (jobResponse.status === 'completed' || jobResponse.status === 'SUCCEEDED') {
    const progressMsg = applyTransformations ? 'Analysis and transform complete' : 'Analysis complete'
    onProgress(50, progressMsg)
  } else {
    // Worker mode: Poll for completion using shared authenticated poller
    const progressMsg = applyTransformations ? 'Processing refactor analysis and transform...' : 'Processing refactor analysis...'
    onProgress(15, progressMsg)
    await pollJobUntilComplete(jobId, apiBaseUrl, {
      maxWaitSeconds: 600,
      pollInterval: 2000,
      onProgress: (progress, message) => {
        // Map polling progress from 15-95%
        const mappedProgress = 15 + (progress * 0.80)
        // Use custom message for transform phase
        const finalMessage = applyTransformations && message.includes('Processing')
          ? 'Analyzing and transforming...'
          : message
        onProgress(mappedProgress, finalMessage)
      },
      signal
    })
  }

  // 3. Get full results
  onProgress(85, 'Retrieving refactor results...')

  let results
  try {
    const resultsResponse = await fetch(`${apiBaseUrl}/coderefactor/${jobId}/results`, { headers: getAuthHeaders(), signal })

    if (!resultsResponse.ok) {
      const errorText = await resultsResponse.text()
      throw new Error(`Failed to get results: ${resultsResponse.status} - ${errorText}`)
    }

    results = await resultsResponse.json()
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Execution cancelled')
    }
    // Use job response if we can't get detailed results
    if (jobResponse) {
      results = jobResponse
    } else {
      throw new Error(`Failed to retrieve results: ${error.message}`)
    }
  }

  // 4. Get recipes (always fetch - useful for viewing what was/could be applied)
  let recipes = null
  try {
    const recipesResponse = await fetch(`${apiBaseUrl}/coderefactor/${jobId}/results/recipes`, { headers: getAuthHeaders(), signal })
    if (recipesResponse.ok) {
      recipes = await recipesResponse.json()
    }
  } catch {
    // Recipes are optional, don't fail if we can't get them
  }

  onProgress(100, 'Code refactor complete')

  // 5. Return formatted result
  return formatResult(jobId, { ...jobResponse, ...results }, recipes, applyTransformations)
}

/**
 * Format the result object for consistent structure
 */
function formatResult(jobId, data, recipes, transformApplied) {
  return {
    // Job tracking
    jobs: {
      coderefactor_job_id: jobId,
    },

    // Status
    success: data.success !== false,
    status: data.status || 'completed',
    phase: transformApplied ? 'full' : 'analyze',
    transform_applied: transformApplied,

    // Execution metrics
    duration_ms: data.duration_ms,

    // Summary statistics
    summary: data.summary || {
      total_classes_analyzed: data.total_classes_analyzed || 0,
      total_patterns_detected: data.total_patterns_detected || 0,
      total_recommendations: data.total_recommendations || 0,
      total_high_severity: data.total_high_severity || 0,
    },

    // Class-level results
    class_results: data.class_results || data.summary?.class_results || [],

    // Artifacts produced
    artifacts: {
      report: data.artifacts?.refactor_report || null,
      markdown: data.artifacts?.refactor_report_md || null,
      rule_patterns: data.artifacts?.rule_patterns || null,
      ai_analysis: data.artifacts?.ai_analysis || null,
      recipes: data.artifacts?.refactor_recipes || null,
    },

    // Recipes for potential follow-up transform
    recipes: recipes,
    recipes_count: recipes?.length || data.recipes_count || 0,

    // Transform results (if applicable)
    transform: data.transform || {
      applied: transformApplied,
      recipes_applied: data.recipes_applied || 0,
      changes_made: data.changes_made || [],
      output_files: data.output_files || [],
    },

    // Class info
    class_name: data.class_name,
    java_file: data.java_file,

    // Paths - important for Code Editor to know where to find files
    // artifacts_path: reports, recipes, etc.
    // output_path: transformed Java files (separate from code_analysis originals)
    artifacts_path: data.artifacts_path,
    output_path: data.output_path || null, // Will be set when transform is implemented

    // Error info if any
    error: data.error || null,
  }
}

export default {
  execute,
}
